import React, { memo, useCallback, useEffect, useMemo } from 'react';
import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { getBooleanField, getEmailField, getPhoneNumberField, getTextField } from '@lender/utils';
import { Box, Button } from '@mui/material';
import {
  AutocompleteCDS,
  CheckboxCDS,
  onAutocompleteChangeInForm,
  TextFieldCDS,
} from '@sharedCR/coreDesignSystem/form';
import AddressAutocompleteCDS from '@sharedCR/coreDesignSystem/form/AddressAutocompleteCDS';
import { SystemPaths } from 'app/configs/routesConfig/system';
import FeeProviderLocationTable from 'app/shared/modules/FeeProviderLocationTable';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { object } from 'yup';

import { FeatureFlag } from 'constants/featureFlags';
import { AddressField, LICENSE_NUMBER_MAX_LENGTH } from 'constants/loanApplication';
import { feeProviderPaidToTypeOptions, statesOptions } from 'constants/options';
import { PAGE_CODES } from 'constants/pageCodes';
import { PHONE_NUMBER_FORMAT } from 'constants/ui';
import useCompanyId from 'hooks/useCompanyId';
import useForm from 'hooks/useForm';
import {
  getConditionalObject,
  normalizeRequestPhoneNumber,
  normalizeViewPhoneNumber,
  replaceEmptyWithNull,
  trimBodyStringValues,
} from 'utils/data';
import { getZipField } from 'utils/validation';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { closeDialogAction } from 'store/global/actions';
import { createFeeProviderAction, updateFeeProviderAction } from 'store/system/actions';
import { selectFeeProviderById } from 'store/system/selectors';

import { FeeProviderFormTestIds } from './FeeProviderForm.testIds';

const buttonStyles = {
  fontSize: '12px',
  width: '70px',
};

const autocompleteInputProps = { margin: 'none' };

const getSchema = ({ isFeeProviderPaidToTypeEnabled, isAddressRequired }) =>
  object().shape({
    name: getTextField(),
    streetAddress: getTextField({ isRequired: isAddressRequired }).nullable(),
    suite: getTextField({ isRequired: false }).nullable(),
    city: getTextField({ isRequired: isAddressRequired }).nullable(),
    state: getTextField({ isRequired: isAddressRequired }).nullable(),
    zipCode: getZipField({ isRequired: isAddressRequired }).nullable(),
    contactPerson: getTextField({ isRequired: false }).nullable(),
    contactPhone: getPhoneNumberField({ isRequired: false }).nullable(),
    contactEmail: getEmailField({ isRequired: false }).nullable(),
    phone: getPhoneNumberField({ isRequired: false }).nullable(),
    email: getEmailField({ isRequired: false }).nullable(),
    licenseNumber: getTextField({ isRequired: isAddressRequired }).nullable(),
    ...getConditionalObject(isFeeProviderPaidToTypeEnabled, () => ({ paidToType: getTextField() })),
    affiliate: getBooleanField(),
    addToServiceProviderList: getBooleanField(),
  });

const getInitialFields = ({ isFeeProviderPaidToTypeEnabled }) => ({
  name: '',
  streetAddress: '',
  suite: '',
  city: '',
  state: undefined,
  zipCode: '',
  contactPerson: '',
  contactPhone: '',
  contactEmail: '',
  phone: '',
  email: '',
  licenseNumber: '',
  ...getConditionalObject(isFeeProviderPaidToTypeEnabled, { paidToType: undefined }),
  affiliate: false,
  addToServiceProviderList: false,
});

const FeeProviderForm = ({
  isDialog = false,
  formRef = null,
  feeProviderCategoryId,
  feeProviderId,
  isLoading,
  setIsLoading,
  onSuccessCreate,
  isLocationsIncluded = true,
}) => {
  const dispatch = useDispatch();
  const { replace } = useHistory();

  const companyId = useCompanyId();

  const isFeeProviderPaidToTypeEnabled = useFeatureToggle(FeatureFlag.EnableFeeProviderPaidToType);

  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.FEE_PROVIDER));

  const feeProvider = useSelector(selectFeeProviderById(feeProviderId));

  const initialFormValues = useMemo(
    () => getInitialFields({ isFeeProviderPaidToTypeEnabled }),
    [isFeeProviderPaidToTypeEnabled],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    setMultipleInForm,
    isFormChanged,
    validateForm,
    setForm,
    setInitialFormValues,
    setValidationErrors,
    validateField,
  } = useForm(initialFormValues);

  useEffect(() => {
    if (!feeProvider || isDialog) {
      return;
    }

    const preparedFeeProvider = {
      ...feeProvider,
      contactPhone: normalizeViewPhoneNumber(feeProvider.contactPhone),
      phone: normalizeViewPhoneNumber(feeProvider.phone),
      state: statesOptions.find(({ value }) => feeProvider.state === value),
      ...getConditionalObject(isFeeProviderPaidToTypeEnabled, () => ({
        paidToType: feeProviderPaidToTypeOptions.find(
          ({ value }) => feeProvider.paidToType === value,
        ),
      })),
    };
    setForm(preparedFeeProvider);
    setInitialFormValues(preparedFeeProvider);
    setValidationErrors({});
  }, [
    feeProvider,
    isDialog,
    setForm,
    setInitialFormValues,
    setValidationErrors,
    isFeeProviderPaidToTypeEnabled,
  ]);

  const isAddressRequired = !isLocationsIncluded;

  useEffect(() => {
    setSchema(getSchema({ isFeeProviderPaidToTypeEnabled, isAddressRequired }));
  }, [setSchema, isFeeProviderPaidToTypeEnabled, isAddressRequired]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onAddressSuggestionSelect = useCallback(
    mapped => {
      const suggestionState = mapped[AddressField.STATE];
      const matchedState = suggestionState
        ? (statesOptions.find(({ value }) => value === suggestionState.value) ?? suggestionState)
        : null;

      const nextStreetAddress = mapped[AddressField.STREET] ?? '';
      const nextCity = mapped[AddressField.CITY] ?? '';
      const nextZipCode = mapped[AddressField.ZIP] ?? '';

      setMultipleInForm({
        streetAddress: nextStreetAddress,
        city: nextCity,
        state: matchedState,
        zipCode: nextZipCode,
      });

      validateField('streetAddress', nextStreetAddress);
      validateField('city', nextCity);
      validateField('state', matchedState);
      validateField('zipCode', nextZipCode);
    },
    [setMultipleInForm, validateField],
  );

  const onCancel = useCallback(() => dispatch(closeDialogAction()), [dispatch]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      const action = feeProviderId ? updateFeeProviderAction : createFeeProviderAction;

      const data = trimBodyStringValues(
        replaceEmptyWithNull(
          feeProviderId
            ? {
                feeProviderId,
                feeProviderData: {
                  ...form,
                  ...getConditionalObject(isFeeProviderPaidToTypeEnabled, {
                    paidToType: form.paidToType?.value,
                  }),
                },
              }
            : {
                ...form,
                feeProviderCategoryId,
                ...getConditionalObject(isFeeProviderPaidToTypeEnabled, {
                  paidToType: form.paidToType?.value,
                }),
                location: {
                  ...form,
                  email: form.email || null,
                  state: form.state?.value,
                  contactPhone: normalizeRequestPhoneNumber(form.contactPhone),
                  phone: normalizeRequestPhoneNumber(form.phone),
                },
              },
          { includeNested: true },
        ),
      );

      setIsLoading(true);
      dispatch(action(data))
        .then(({ payload }) => {
          if (onSuccessCreate) {
            return onSuccessCreate(payload);
          }

          if (isDialog) {
            dispatch(closeDialogAction());
            SystemPaths.feeProvider.navigateWith(replace, {
              feeProviderCategoryId,
              feeProviderId: payload.feeProviderId,
            });
          }
        })
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [
      dispatch,
      feeProviderCategoryId,
      feeProviderId,
      form,
      isDialog,
      onSuccessCreate,
      replace,
      setIsLoading,
      validateForm,
      isFeeProviderPaidToTypeEnabled,
    ],
  );

  const isCompanyManageAllowed = !feeProviderId || companyId === feeProvider?.companyId;

  const isFormDisabled = !isManageAllowed || isLoading || !isCompanyManageAllowed;

  return (
    <form
      noValidate
      className="w-full grid grid-cols-3 gap-24 pt-16"
      data-ischanged={isFormChanged}
      data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM}
      ref={formRef}
      onSubmit={onSubmit}
    >
      <TextFieldCDS
        autoFocus
        fullWidth
        required
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_NAME_INPUT}
        disabled={isFormDisabled}
        error={!!validationErrors.name}
        helperText={validationErrors.name}
        id="name"
        label="Provider Name"
        maxLength={100}
        name="name"
        type="text"
        value={form.name}
        variant="outlined"
        onChange={handleChange}
      />

      <AddressAutocompleteCDS
        fullWidth
        autoComplete="off"
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_STREET_ADDRESS_INPUT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.streetAddress}
        helperText={validationErrors.streetAddress}
        id="streetAddress"
        label="Street Address"
        maxLength={150}
        name="streetAddress"
        required={isAddressRequired}
        type="text"
        value={form.streetAddress}
        variant="outlined"
        onChange={handleChange}
        onSelect={onAddressSuggestionSelect}
      />

      <TextFieldCDS
        fullWidth
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_SUITE_INPUT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.suite}
        helperText={validationErrors.suite}
        id="suite"
        label="Suite"
        maxLength={150}
        name="suite"
        type="text"
        value={form.suite}
        variant="outlined"
        onChange={handleChange}
      />

      <TextFieldCDS
        fullWidth
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_CITY_INPUT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.city}
        helperText={validationErrors.city}
        id="city"
        label="City"
        maxLength={100}
        name="city"
        required={isAddressRequired}
        type="text"
        value={form.city}
        variant="outlined"
        onChange={handleChange}
      />

      <AutocompleteCDS
        fullWidth
        dataTestId={FeeProviderFormTestIds.FEE_PROVIDER_FORM_STATE_SELECT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.state}
        helperText={validationErrors.state}
        inputProps={autocompleteInputProps}
        label="State"
        name="state"
        options={statesOptions}
        required={isAddressRequired}
        value={form.state}
        variant="outlined"
        onChange={onAutocompleteChange}
      />

      <TextFieldCDS
        fullWidth
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_ZIP_CODE_INPUT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.zipCode}
        helperText={validationErrors.zipCode}
        id="zipCode"
        label="ZIP Code"
        maxLength={5}
        name="zipCode"
        required={isAddressRequired}
        type="text"
        value={form.zipCode}
        variant="outlined"
        onChange={handleChange}
      />

      <TextFieldCDS
        fullWidth
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_CONTACT_PERSON_INPUT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.contactPerson}
        helperText={validationErrors.contactPerson}
        id="contactPerson"
        label="Contact Person"
        maxLength={100}
        name="contactPerson"
        type="text"
        value={form.contactPerson}
        variant="outlined"
        onChange={handleChange}
      />

      <NumberFormat
        fullWidth
        customInput={TextFieldCDS}
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_CONTACT_PHONE_INPUT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.contactPhone}
        format={PHONE_NUMBER_FORMAT}
        helperText={validationErrors.contactPhone}
        label="Contact Phone"
        mask="_"
        name="contactPhone"
        type="tel"
        value={form.contactPhone}
        variant="outlined"
        onChange={handleChange}
      />

      <TextFieldCDS
        fullWidth
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_CONTACT_EMAIL_INPUT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.contactEmail}
        helperText={validationErrors.contactEmail}
        id="contact-email"
        label="Contact Email"
        maxLength={100}
        name="contactEmail"
        type="text"
        value={form.contactEmail}
        variant="outlined"
        onChange={handleChange}
      />

      <NumberFormat
        fullWidth
        customInput={TextFieldCDS}
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_PHONE_INPUT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.phone}
        format={PHONE_NUMBER_FORMAT}
        helperText={validationErrors.phone}
        label="Phone"
        mask="_"
        name="phone"
        type="tel"
        value={form.phone}
        variant="outlined"
        onChange={handleChange}
      />

      <TextFieldCDS
        fullWidth
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_EMAIL_INPUT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.email}
        helperText={validationErrors.email}
        id="email"
        label="Email"
        maxLength={100}
        name="email"
        type="text"
        value={form.email}
        variant="outlined"
        onChange={handleChange}
      />

      <TextFieldCDS
        fullWidth
        data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_LICENSE_NUMBER_INPUT}
        disabled={isFormDisabled || isLocationsIncluded}
        error={!!validationErrors.licenseNumber}
        helperText={validationErrors.licenseNumber}
        id="licenseNumber"
        label="License Number"
        maxLength={LICENSE_NUMBER_MAX_LENGTH}
        name="licenseNumber"
        required={isAddressRequired}
        type="text"
        value={form.licenseNumber}
        variant="outlined"
        onChange={handleChange}
      />

      {isFeeProviderPaidToTypeEnabled && (
        <AutocompleteCDS
          fullWidth
          required
          dataTestId={FeeProviderFormTestIds.FEE_PROVIDER_FORM_PAID_TO_TYPE_SELECT}
          disabled={isFormDisabled}
          error={!!validationErrors.paidToType}
          helperText={validationErrors.paidToType}
          inputProps={autocompleteInputProps}
          label="Paid to Type"
          name="paidToType"
          options={feeProviderPaidToTypeOptions}
          value={form.paidToType}
          variant="outlined"
          onChange={onAutocompleteChange}
        />
      )}

      <div className="flex flex-col gap-16 col-span-3">
        <CheckboxCDS
          fullWidth
          data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_AFFILIATE_CHECKBOX}
          disabled={isFormDisabled}
          error={!!validationErrors.affiliate}
          helperText={validationErrors.affiliate}
          label="Affiliate"
          name="affiliate"
          value={form.affiliate}
          onChange={handleChange}
        />

        <CheckboxCDS
          fullWidth
          data-testid={
            FeeProviderFormTestIds.FEE_PROVIDER_FORM_ADD_TO_SERVICE_PROVIDER_LIST_CHECKBOX
          }
          disabled={isFormDisabled}
          error={!!validationErrors.addToServiceProviderList}
          helperText={validationErrors.addToServiceProviderList}
          label="Add to Service Provider List"
          name="addToServiceProviderList"
          value={form.addToServiceProviderList}
          onChange={handleChange}
        />
      </div>

      {isLocationsIncluded && (
        <Box className="col-span-3">
          <FeeProviderLocationTable
            feeProviderId={feeProviderId}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        </Box>
      )}

      <Box className="flex justify-end col-span-3 gap-x-24">
        <Button
          data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_CANCEL_BUTTON}
          disabled={isFormDisabled}
          sx={buttonStyles}
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>

        <Button
          data-testid={FeeProviderFormTestIds.FEE_PROVIDER_FORM_SUBMIT_BUTTON}
          disabled={isFormDisabled || !isFormChanged}
          sx={buttonStyles}
          type="submit"
        >
          Add
        </Button>
      </Box>
    </form>
  );
};

FeeProviderForm.propTypes = {
  feeProviderCategoryId: PropTypes.string.isRequired,
  formRef: PropTypes.shape({ current: PropTypes.object }),
  feeProviderId: PropTypes.string,
  isDialog: PropTypes.bool,
  isLocationsIncluded: PropTypes.bool,
  onSuccessCreate: PropTypes.func,
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(FeeProviderForm);
